import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramTotalHeat } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.heatTransferTotal.v1';

type Fields = { airFlow: string; enthalpyDiff: string; totalHeat: string };
const EMPTY: Fields = { airFlow: '', enthalpyDiff: '', totalHeat: '' };

/**
 * Total Heat Transfer — Q_total = 4.5 × CFM × ΔH
 * Solves for whichever of CFM / ΔH / Q_total is missing given the other two.
 */
const HeatTransferTotalHeatTransferScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp, isImperial } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const cfm = toImp(fields.airFlow, 'flow');
    const dh = toImp(fields.enthalpyDiff, 'enthalpy');
    const qt = toImp(fields.totalHeat, 'heatRate');

    if (fields.totalHeat === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      setSolvedFor('totalHeat');
      set({ totalHeat: fromImp(4.5 * cfm * dh, 'heatRate', 0) });
    } else if (fields.airFlow === '') {
      if (dh === 0) { setError('Enthalpy difference cannot be zero.'); return; }
      setSolvedFor('airFlow');
      set({ airFlow: fromImp(qt / (4.5 * dh), 'flow', 1) });
    } else if (fields.enthalpyDiff === '') {
      if (cfm <= 0) { setError('Air flow must be greater than zero.'); return; }
      setSolvedFor('enthalpyDiff');
      set({ enthalpyDiff: fromImp(qt / (4.5 * cfm), 'enthalpy', 2) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.airFlow && { label: 'Air Flow', value: fields.airFlow, unit: unit('flow'), emphasis: solvedFor === 'airFlow' },
    fields.enthalpyDiff && { label: 'Enthalpy Difference', value: fields.enthalpyDiff, unit: unit('enthalpy'), emphasis: solvedFor === 'enthalpyDiff' },
    fields.totalHeat && { label: 'Total Heat Transfer', value: fields.totalHeat, unit: unit('heatRate'), emphasis: solvedFor === 'totalHeat' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Total Heat Transfer"
      subtitle="Calculate total heat transfer rate from air flow and enthalpy difference"
      formula={
        <>
          <FormulaLine>{isImperial ? 'Q_total = 4.5 × CFM × ΔH' : 'Q_total = 1.20 × L/s × ΔH'}</FormulaLine>
          <FormulaNote>Q_total = Total Heat Transfer ({unit('heatRate')}), {isImperial ? 'CFM' : 'L/s'} = Air Flow Rate ({unit('flow')}), ΔH = Enthalpy Difference ({unit('enthalpy')})</FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter={isImperial ? 'Formula: Q_total = 4.5 × CFM × ΔH' : 'Formula: Q_total = 1.20 × L/s × ΔH'}
      diagram={<DiagramTotalHeat />}
      outputSlots={{
        cfm: fields.airFlow ? toImp(fields.airFlow, 'flow') : undefined,
        enthalpy_btu_lb: fields.enthalpyDiff ? toImp(fields.enthalpyDiff, 'enthalpy') : undefined,
        q_btuh: fields.totalHeat ? toImp(fields.totalHeat, 'heatRate') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.cfm != null && !fields.airFlow) patch.airFlow = fromImp(values.cfm, 'flow', 1);
        if (values.enthalpy_btu_lb != null && !fields.enthalpyDiff) patch.enthalpyDiff = fromImp(values.enthalpy_btu_lb, 'enthalpy', 2);
        if (values.q_btuh != null && !fields.totalHeat) patch.totalHeat = fromImp(values.q_btuh, 'heatRate', 0);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Air Flow"
          value={fields.airFlow}
          onChangeText={(v) => set({ airFlow: v })}
          rightText={unit('flow')}
          maxDecimals={1}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Enthalpy Difference"
          value={fields.enthalpyDiff}
          onChangeText={(v) => set({ enthalpyDiff: v })}
          rightText={unit('enthalpy')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Total Heat Transfer"
          value={fields.totalHeat}
          onChangeText={(v) => set({ totalHeat: v })}
          rightText={unit('heatRate')}
          maxDecimals={0}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default HeatTransferTotalHeatTransferScreen;
